/**
 * InlineSelect Component
 *
 * Searchable dropdown select for toolbar filters.
 */

import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import clsx from 'clsx';
import { useClickOutside } from '@/hooks/useClickOutside';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import { DropdownShell } from './DropdownShell';

export type SelectOption = {
  value: string;
  label: string;
  icon?: ReactNode;
  hint?: string;
};

export interface InlineSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  icon?: ReactNode;
  searchable?: boolean;
  className?: string;
  minWidthClass?: string;
  compact?: boolean;
}

export function InlineSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Seleziona...',
  icon,
  searchable = true,
  className,
  minWidthClass = 'min-w-[120px] sm:min-w-[150px]',
  compact = false,
}: InlineSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useClickOutside(
    [containerRef, dropdownRef],
    () => {
      setOpen(false);
      setQuery('');
      setDropdownPosition(null);
    },
    open
  );

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 140);
      const pos = clampToViewport(rect.left, rect.bottom + 6, width, 260);
      setDropdownPosition({ ...pos, width });
    } else {
      setDropdownPosition(null);
    }
  }, [open]);

  const filtered = useMemo(() => {
    return options.filter((opt) => opt.label.toLowerCase().includes(query.toLowerCase()));
  }, [options, query]);

  const selected = options.find((opt) => opt.value === value);

  const isActive = !!selected && value !== 'none' && value !== placeholder;

  return (
    <>
      <div ref={containerRef} className={compact ? 'relative' : clsx('flex-1 min-w-0', minWidthClass, className)}>
        {compact ? (
          <button
            onClick={() => setOpen((prev) => !prev)}
            title={label}
            className={clsx(
              'w-7 h-7 flex items-center justify-center rounded-md border transition-colors',
              isActive
                ? 'border-accent-violet/50 bg-accent-violet/10 text-accent-violet'
                : 'border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover text-text-tertiary'
            )}
          >
            {icon}
            {isActive && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-accent-violet pointer-events-none" />
            )}
          </button>
        ) : (
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover transition-colors min-h-[36px] w-full"
        >
          {icon && <span className="text-text-tertiary">{icon}</span>}
          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-[9px] uppercase tracking-wide text-text-quaternary font-semibold leading-none">
              {label}
            </span>
            <span className="text-[10px] sm:text-[11px] text-text-secondary font-semibold leading-tight truncate">
              {selected ? selected.label : placeholder}
            </span>
          </div>
          <ChevronDown
            className={clsx(
              'w-3 h-3 text-text-quaternary transition-transform ml-1 shrink-0',
              open && 'rotate-180'
            )}
          />
        </button>
        )}
      </div>

      {open &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 animate-scale-in origin-top"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            <DropdownShell>
              {searchable && (
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border-primary">
                  <Search className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 bg-transparent text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none"
                    placeholder="Cerca..."
                  />
                </div>
              )}
              <div className="max-h-64 overflow-y-auto py-1">
                {filtered.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-text-quaternary">Nessun risultato</div>
                )}
                {filtered.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-surface-hover transition-colors',
                      opt.value === value && 'bg-surface-hover/80 text-text-primary'
                    )}
                  >
                    {opt.icon && <span className="text-text-tertiary">{opt.icon}</span>}
                    <div className="flex flex-col">
                      <span className="text-text-primary">{opt.label}</span>
                      {opt.hint && (
                        <span className="text-[10px] text-text-quaternary">{opt.hint}</span>
                      )}
                    </div>
                    <div className="flex-1" />
                    {opt.value === value && <Check className="w-3.5 h-3.5 text-accent-violet" />}
                  </button>
                ))}
              </div>
            </DropdownShell>
          </div>,
          document.body
        )}
    </>
  );
}
